import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const Box = ({ title, text, tone }) => (
  <div className={`rounded-lg border p-3 ${tone}`}>
    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider">{title}</p>
    <p className="max-h-48 overflow-y-auto whitespace-pre-line font-serif text-sm leading-relaxed text-slate-200">{text || '—'}</p>
  </div>
);

export const ReformAlerts = ({ matter, onUpdated }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const pending = (matter.reform_alerts || []).filter((a) => !a.read);
  if (!pending.length) return null;

  const act = async (a, update) => {
    setBusy(a.id);
    try {
      await api.post(`/legislative/reform-alerts/${matter.id}/${a.id}`, { update_citation: update });
      toast.success(update ? 'Cita actualizada al texto vigente' : 'Aviso marcado como revisado');
      if (pending.length === 1) setOpen(false);
      onUpdated && onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar'); } finally { setBusy(''); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`reform-alert-badge-${matter.id}`}
        className="mt-3 flex w-full items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-left text-xs text-amber-200 hover:bg-amber-500/20">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span><b>{pending.length} {pending.length === 1 ? 'artículo citado cambió' : 'artículos citados cambiaron'}</b> en la fuente oficial: {pending.map((a) => `${a.articulo} ${a.norma}`).join(' · ').slice(0, 120)}</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid={`reform-alert-dialog-${matter.id}`}>
          <DialogHeader>
            <DialogTitle>Reformas en normas citadas</DialogTitle>
            <DialogDescription>Caso «{matter.title}». Revisa el cambio antes de presentar el próximo escrito.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] space-y-5 overflow-y-auto pr-1">
            {pending.map((a) => (
              <div key={a.id} className="space-y-2" data-testid={`reform-alert-${a.id}`}>
                <p className="text-sm font-semibold text-white">{a.articulo} · {a.norma} <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-[11px] text-amber-200">{a.status === 'modificado' ? 'Modificado' : 'Derogado o no encontrado'}</span></p>
                <div className="grid gap-2 md:grid-cols-2">
                  <Box title="Texto que citaste" text={a.cited_text || a.old_text} tone="border-slate-700 bg-slate-950/60 text-slate-400" />
                  <Box title="Texto oficial vigente" text={a.new_text || 'El artículo ya no aparece en la fuente oficial.'} tone="border-emerald-600/40 bg-emerald-950/30 text-emerald-300" />
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button onClick={() => act(a, false)} disabled={!!busy} data-testid={`reform-dismiss-btn-${a.id}`} className="flex items-center gap-1 rounded-lg border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800"><Check className="h-3.5 w-3.5" /> Marcar revisado</button>
                  {a.new_text && <button onClick={() => act(a, true)} disabled={!!busy} data-testid={`reform-update-btn-${a.id}`} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"><RefreshCw className={`h-3.5 w-3.5 ${busy === a.id ? 'animate-spin' : ''}`} /> Actualizar la cita al texto vigente</button>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
